import * as React from 'react';
import { Button, type ButtonProps } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

export interface IconButtonProps extends ButtonProps {
  /** Tooltip text shown on hover/focus. Also used as the accessible label. */
  tooltip: string;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
}

/**
 * Icon-only action button with a mandatory accessible tooltip.
 * Use this for every button that renders only a lucide icon (no visible text).
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { tooltip, tooltipSide = 'top', size = 'icon', variant = 'ghost', 'aria-label': ariaLabel, ...props },
    ref,
  ) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button ref={ref} size={size} variant={variant} aria-label={ariaLabel ?? tooltip} {...props} />
      </TooltipTrigger>
      <TooltipContent side={tooltipSide}>{tooltip}</TooltipContent>
    </Tooltip>
  ),
);
IconButton.displayName = 'IconButton';

export { IconButton };
